<script setup lang="ts">
/**
 * Module: web/src/views/ResetView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * Password reset landing -- the emailed magic-link points here as
 * /reset?token=... (doc 04 sect 8.6). POST /auth/reset { token, new_pw };
 * 400 = invalid/expired/used token or policy failure.
 */
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { apiPost, ApiError } from '@/composables/useApi';
import AppIcon from '@/components/AppIcon.vue';

const route = useRoute();

const token = computed(() => (typeof route.query.token === 'string' ? route.query.token : ''));
const newPw = ref('');
const confirmPw = ref('');
const showNew = ref(false);
const submitting = ref(false);
const done = ref(false);
const errorMessage = ref('');

const MIN_LENGTH = 12;

async function submit() {
  if (submitting.value) return;
  errorMessage.value = '';
  if (newPw.value.length < MIN_LENGTH) {
    errorMessage.value = `New password must be at least ${MIN_LENGTH} characters.`;
    return;
  }
  if (newPw.value !== confirmPw.value) {
    errorMessage.value = 'New password and confirmation do not match.';
    return;
  }
  submitting.value = true;
  try {
    await apiPost<void>('/auth/reset', { token: token.value, new_pw: newPw.value });
    done.value = true;
  } catch (e) {
    if (e instanceof ApiError && e.status === 400) {
      errorMessage.value = e.detail ?? 'This reset link is invalid or has expired. Request a new one.';
    } else {
      errorMessage.value = 'Reset failed. Try again.';
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen flex flex-col items-center justify-center px-4 py-8">
    <div class="w-full max-w-sm">
      <h1 class="text-xl font-bold text-fg mb-1">Reset password</h1>

      <!-- No token in the URL -- the link is broken/truncated -->
      <div v-if="!token" class="mt-5 space-y-5">
        <div class="alert-error" role="alert">
          This reset link is missing its token. Open the link from your email again, or request a new one.
        </div>
        <RouterLink to="/reset-request" class="btn-secondary w-full">Request a new link</RouterLink>
      </div>

      <div v-else-if="done" class="mt-5 space-y-5">
        <div class="alert-success flex items-start gap-2.5" role="status">
          <AppIcon name="check" class="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>Password reset. Sign in with your new password.</span>
        </div>
        <RouterLink to="/login" class="btn-primary w-full">Go to sign in</RouterLink>
      </div>

      <form v-else class="space-y-4 mt-5" novalidate @submit.prevent="submit">
        <p class="text-sm text-fg-muted">
          Choose a new password -- at least {{ MIN_LENGTH }} characters.
        </p>
        <div v-if="errorMessage" class="alert-error" role="alert">{{ errorMessage }}</div>

        <div>
          <label class="field-label" for="rs-new">New password</label>
          <div class="relative">
            <input
              id="rs-new"
              v-model="newPw"
              class="field-input pr-12"
              :type="showNew ? 'text' : 'password'"
              autocomplete="new-password"
              :disabled="submitting"
            />
            <button
              type="button"
              class="absolute inset-y-0 right-0 px-3 text-fg-muted hover:text-fg"
              :aria-label="showNew ? 'Hide password' : 'Show password'"
              @click="showNew = !showNew"
            >
              <AppIcon :name="showNew ? 'eye-off' : 'eye'" class="w-5 h-5" />
            </button>
          </div>
        </div>

        <div>
          <label class="field-label" for="rs-confirm">Confirm new password</label>
          <input
            id="rs-confirm"
            v-model="confirmPw"
            class="field-input"
            :type="showNew ? 'text' : 'password'"
            autocomplete="new-password"
            :disabled="submitting"
          />
        </div>

        <button type="submit" class="btn-primary w-full" :disabled="submitting">
          {{ submitting ? 'Resetting…' : 'Reset password' }}
        </button>
      </form>
    </div>
  </div>
</template>
